import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit2, Trash2, Shield, ShieldCheck, Eye, EyeOff, Check, X, Search } from 'lucide-react'
import api from '@/lib/api'

interface User {
  id: number
  username: string
  email: string
  full_name: string | null
  is_active: boolean
  is_admin: boolean
  last_login: string | null
  created_at: string | null
  permissions?: string[]
}

interface Section {
  code: string
  name: string
  description: string
  display_order: number
}

export default function UsersPage() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    is_admin: false,
    is_active: true,
  })

  // Permissions state
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  // Query users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users/')
      return response.data as { users: User[]; total: number }
    },
  })

  // Query sections
  const { data: sectionsData } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await api.get('/users/sections/list')
      return response.data as { sections: Section[] }
    },
  })

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post('/users/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowModal(false)
      resetForm()
    },
  })

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<typeof formData> }) => {
      const response = await api.put(`/users/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowModal(false)
      resetForm()
    },
  })

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/users/${id}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  // Update permissions mutation
  const permissionsMutation = useMutation({
    mutationFn: async ({ id, permissions }: { id: number; permissions: string[] }) => {
      const response = await api.put(`/users/${id}/permissions`, { permissions })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowPermissionsModal(false)
      setSelectedUser(null)
    },
  })

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      is_admin: false,
      is_active: true,
    })
    setSelectedUser(null)
    setShowPassword(false)
  }

  const handleOpenModal = (user?: User) => {
    if (user) {
      setSelectedUser(user)
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        full_name: user.full_name || '',
        is_admin: user.is_admin,
        is_active: user.is_active,
      })
    } else {
      resetForm()
    }
    setShowModal(true)
  }

  const handleOpenPermissions = async (user: User) => {
    setSelectedUser(user)
    // Fetch user with permissions
    const response = await api.get(`/users/${user.id}`)
    const userData = response.data as User
    setSelectedPermissions(userData.permissions || [])
    setShowPermissionsModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedUser) {
      // Update - only send fields that changed
      const updateData: Partial<typeof formData> = {}
      if (formData.full_name !== selectedUser.full_name) updateData.full_name = formData.full_name
      if (formData.is_admin !== selectedUser.is_admin) updateData.is_admin = formData.is_admin
      if (formData.is_active !== selectedUser.is_active) updateData.is_active = formData.is_active
      if (formData.password) updateData.password = formData.password

      updateMutation.mutate({ id: selectedUser.id, data: updateData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleSavePermissions = () => {
    if (selectedUser) {
      permissionsMutation.mutate({ id: selectedUser.id, permissions: selectedPermissions })
    }
  }

  const togglePermission = (code: string) => {
    setSelectedPermissions(prev => {
      if (prev.includes(code)) {
        return prev.filter(p => p !== code)
      }
      return [...prev, code]
    })
  }

  const filteredUsers = usersData?.users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  // Group sections by parent, maintaining display_order
  const groupedSections = (() => {
    if (!sectionsData?.sections) return []

    // Sort sections by display_order first
    const sorted = [...sectionsData.sections].sort((a, b) => a.display_order - b.display_order)

    // Group: parent sections (no dot) with their children
    const groups: { parent: Section; children: Section[] }[] = []
    const parentMap = new Map<string, { parent: Section; children: Section[] }>()

    for (const section of sorted) {
      if (!section.code.includes('.')) {
        // This is a parent section
        const group = { parent: section, children: [] }
        groups.push(group)
        parentMap.set(section.code, group)
      } else {
        // This is a child section
        const parentCode = section.code.split('.')[0]
        const group = parentMap.get(parentCode)
        if (group) {
          group.children.push(section)
        }
      }
    }

    return groups
  })()

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Gestión de Usuarios
        </h1>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuevo Usuario
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Último acceso
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="table-row">
                    <td className="table-cell">
                      <div>
                        <div className="font-medium text-slate-900 dark:text-white">
                          {user.username}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {user.full_name || '-'}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">{user.email}</td>
                    <td className="table-cell">
                      {user.is_admin ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                          <ShieldCheck className="w-3 h-3" />
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">
                          <Shield className="w-3 h-3" />
                          Usuario
                        </span>
                      )}
                    </td>
                    <td className="table-cell">
                      {user.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <Check className="w-3 h-3" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-red-800 dark:bg-danger-900/30 dark:text-danger-400">
                          <X className="w-3 h-3" />
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="table-cell text-sm text-slate-500 dark:text-slate-400">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleString('es-ES')
                        : 'Nunca'}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!user.is_admin && (
                          <button
                            onClick={() => handleOpenPermissions(user)}
                            className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                            title="Editar permisos"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenModal(user)}
                          className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                          title="Editar usuario"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('¿Estás seguro de eliminar este usuario?')) {
                              deleteMutation.mutate(user.id)
                            }
                          }}
                          className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-danger-600 dark:hover:text-red-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                          title="Eliminar usuario"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {selectedUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nombre de usuario
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="input"
                    required
                    disabled={!!selectedUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    required
                    disabled={!!selectedUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Contraseña {selectedUser && '(dejar en blanco para mantener)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input pr-10"
                      required={!selectedUser}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_admin}
                      onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Administrador</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Activo</span>
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    resetForm()
                  }}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn btn-primary"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Permisos de {selectedUser.full_name || selectedUser.username}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Selecciona las secciones a las que el usuario tendrá acceso
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {groupedSections.map(({ parent, children }) => (
                  <div key={parent.code} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                    {/* Parent section header */}
                    <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-700/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-slate-200 dark:border-slate-700">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(parent.code)}
                        onChange={() => togglePermission(parent.code)}
                        className="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {parent.name}
                        </span>
                        {parent.description && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {parent.description}
                          </p>
                        )}
                      </div>
                      {children.length > 0 && (
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          {children.length} subsecciones
                        </span>
                      )}
                    </label>
                    {/* Child sections */}
                    {children.length > 0 && (
                      <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                        {children.map(section => (
                          <label
                            key={section.code}
                            className="flex items-center gap-3 px-4 py-3 pl-12 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(section.code)}
                              onChange={() => togglePermission(section.code)}
                              className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {section.name}
                              </span>
                              {section.description && (
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {section.description}
                                </p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowPermissionsModal(false)
                  setSelectedUser(null)
                }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={permissionsMutation.isPending}
                className="btn btn-primary"
              >
                {permissionsMutation.isPending ? 'Guardando...' : 'Guardar permisos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
